import type { FC, ReactNode } from 'react'
import { UserAvatar } from '@/components/ui/user-avatar'
import { getNotificationRowCopy } from '@/lib/notifications/notification-copy'
import { cn } from '@/lib/utils'
import {
  AtIcon,
  BellIcon,
  ChatIcon,
  CheckCircleIcon,
  ClockCountdownIcon,
  HeartbeatIcon,
  ListChecksIcon,
  MicrophoneIcon,
  RobotIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { formatDistanceToNowStrict, isToday, isYesterday } from 'date-fns'

import type {
  Notification,
  NotificationFeedResponse,
  NotificationResolution,
} from '@scrambl/shared'
import { NotificationPriority, NotificationType } from '@scrambl/shared'

import '@/styles/notification-feed.css'

// ===== per-type presentation =====

const TIMESHEET_TYPES = new Set<NotificationType>([
  NotificationType.TIMESHEET_APPROVAL_DEADLINE,
  NotificationType.TIMESHEET_SUBMITTED,
  NotificationType.TIMESHEET_ENTRY_SUBMITTED,
  NotificationType.TIMESHEET_CLOSE_REMINDER,
  NotificationType.TIMESHEET_MISSING_REMINDER,
  NotificationType.TIMESHEET_APPROVED,
  NotificationType.TIMESHEET_REJECTED,
  NotificationType.TIMESHEET_ENTRY_APPROVED,
  NotificationType.TIMESHEET_ENTRY_REJECTED,
])

function getActionIcon(type: NotificationType): ReactNode {
  if (TIMESHEET_TYPES.has(type)) return <ClockCountdownIcon weight="fill" />
  switch (type) {
    case NotificationType.TASK_OVERDUE:
    case NotificationType.TASK_DUE_SOON:
    case NotificationType.TASK_SLA_BREACHED:
      return <WarningCircleIcon weight="fill" />
    case NotificationType.AGENT_REVIEW_REQUESTED:
      return <RobotIcon weight="fill" />
    case NotificationType.ENTITY_HEALTH_AT_RISK:
    case NotificationType.ENTITY_HEALTH_DECLINED:
    case NotificationType.ENTITY_HEALTH_IMPROVED:
    case NotificationType.DEAL_HEALTH_AT_RISK:
      return <HeartbeatIcon weight="fill" />
    case NotificationType.MEETING_NOTES_READY:
    case NotificationType.MEETING_NOTES_FAILED:
      return <MicrophoneIcon weight="fill" />
    default:
      return <BellIcon weight="fill" />
  }
}

function getActionTone(notification: Notification): 'bad' | 'warn' | 'accent' {
  if (notification.priority === NotificationPriority.URGENT) return 'bad'
  if (notification.priority === NotificationPriority.HIGH) return 'warn'
  return 'accent'
}

export function getActionCta(type: NotificationType): string {
  switch (type) {
    case NotificationType.TIMESHEET_APPROVAL_DEADLINE:
    case NotificationType.TIMESHEET_SUBMITTED:
    case NotificationType.TIMESHEET_ENTRY_SUBMITTED:
    case NotificationType.AGENT_REVIEW_REQUESTED:
      return 'Review'
    // Missing hours are fixed by logging time; a closing period is reviewed.
    // "Submit" promised an action that doesn't exist in workspaces that don't
    // require timesheet approval.
    case NotificationType.TIMESHEET_MISSING_REMINDER:
      return 'Log time'
    case NotificationType.TIMESHEET_CLOSE_REMINDER:
      return 'Review'
    default:
      return 'Open'
  }
}

function getModuleGlyph(notification: Notification): ReactNode {
  if (TIMESHEET_TYPES.has(notification.type)) return <ClockCountdownIcon />
  switch (notification.type) {
    case NotificationType.TASK_MENTION:
    case NotificationType.COMMENT_MENTION:
    case NotificationType.NOTE_MENTION:
    case NotificationType.TICKET_MENTION:
      return <AtIcon />
    case NotificationType.COMMENT_REPLY:
    case NotificationType.TASK_COMMENT_ADDED:
    case NotificationType.TICKET_COMMENT_ADDED:
      return <ChatIcon />
    case NotificationType.TASK_ASSIGNED:
    case NotificationType.TASK_STATUS_CHANGED:
      return <ListChecksIcon />
    default:
      return <BellIcon />
  }
}

function getActorName(notification: Notification): string | null {
  const actor = notification.actor
  if (!actor) return null
  return (
    actor.displayName ||
    [actor.firstName, actor.lastName].filter(Boolean).join(' ') ||
    actor.email ||
    null
  )
}

function getResolution(
  notification: Notification,
): NotificationResolution | null {
  const resolution = (notification.data as Record<string, unknown> | null)
    ?.resolution as NotificationResolution | undefined
  return resolution?.outcome ? resolution : null
}

/** Prefer the authored excerpt over the verbose event message in mention rows. */
export function getNotificationQuote(
  notification: Notification,
): string | null {
  const data = (notification.data ?? {}) as Record<string, unknown>
  const snippetKeysByType: Partial<
    Record<NotificationType, Array<keyof typeof data>>
  > = {
    [NotificationType.TASK_MENTION]: ['descriptionSnippet'],
    [NotificationType.COMMENT_MENTION]: ['commentSnippet'],
    [NotificationType.TICKET_MENTION]: [
      'commentSnippet',
      'descriptionSnippet',
      'ticketDescription',
    ],
    [NotificationType.NOTE_MENTION]: ['contentSnippet'],
    [NotificationType.COMMENT_REPLY]: ['commentSnippet'],
  }

  const keys = snippetKeysByType[notification.type] ?? []
  for (const key of keys) {
    const value = data[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }

  return notification.message?.trim() || null
}

function timeAgo(date: Date | string): string {
  return formatDistanceToNowStrict(new Date(date), { addSuffix: true })
}

function getDayBucket(date: Date | string): string {
  const d = new Date(date)
  if (isToday(d)) return 'Today'
  if (isYesterday(d)) return 'Yesterday'
  const weekAgo = new Date()
  weekAgo.setDate(weekAgo.getDate() - 7)
  return d >= weekAgo ? 'This week' : 'Earlier'
}

export interface ActionNotificationGroup {
  key: string
  notifications: Notification[]
}

/**
 * Approval deadlines are distinct backend situations (member + pay period),
 * but presenting each as a separate card overwhelms an approver. Keep the
 * records separate and collapse them into one display group at the first
 * deadline's sorted position.
 */
export function groupActionNotifications(
  notifications: Notification[],
): ActionNotificationGroup[] {
  const approvalDeadlines = notifications.filter(
    notification =>
      notification.type === NotificationType.TIMESHEET_APPROVAL_DEADLINE,
  )
  if (approvalDeadlines.length < 2) {
    return notifications.map(notification => ({
      key: notification.id,
      notifications: [notification],
    }))
  }

  let insertedApprovalGroup = false
  return notifications.flatMap(notification => {
    if (notification.type !== NotificationType.TIMESHEET_APPROVAL_DEADLINE) {
      return [{ key: notification.id, notifications: [notification] }]
    }
    if (insertedApprovalGroup) return []
    insertedApprovalGroup = true
    return [
      {
        key: `timesheet-approval-deadlines:${approvalDeadlines
          .map(item => item.id)
          .join(':')}`,
        notifications: approvalDeadlines,
      },
    ]
  })
}

// ===== rows =====

/** Rows are <button>s, so the dismiss control lives in a wrapper, not nested. */
function DismissableItem({
  children,
  onDismiss,
  dismissLabel = 'Dismiss notification',
}: {
  children: ReactNode
  onDismiss?: () => void
  dismissLabel?: string
}) {
  if (!onDismiss) return <>{children}</>
  return (
    <div className="nfd-item-wrap">
      {children}
      <button
        type="button"
        className="nfd-dismiss"
        title="Dismiss"
        aria-label={dismissLabel}
        onClick={event => {
          event.stopPropagation()
          onDismiss()
        }}
      >
        <XIcon weight="bold" />
      </button>
    </div>
  )
}

function ActionStack({
  notifications,
  onSelect,
}: {
  notifications: Notification[]
  onSelect: (notifications: Notification[]) => void
}) {
  const notification = notifications[0]
  const isApprovalGroup =
    notifications.length > 1 &&
    notifications.every(
      item => item.type === NotificationType.TIMESHEET_APPROVAL_DEADLINE,
    )
  const allOverdue =
    isApprovalGroup &&
    notifications.every(item => {
      const data = item.data as Record<string, unknown> | null
      return data?.overdue === true
    })
  const memberCount = new Set(
    notifications
      .map(item => {
        const data = item.data as Record<string, unknown> | null
        return data?.targetUserId
      })
      .filter((value): value is string => typeof value === 'string'),
  ).size
  const mergedCount = notification.unreadMessageCount ?? 1
  const lastAt =
    notification.lastMessageAt ??
    notification.updatedAt ??
    notification.createdAt
  // Verb-first copy where the payload can describe the event; types that only
  // carry the stored sentence come back unchanged (`secondary === null`).
  const rowCopy = getNotificationRowCopy(notification)
  const isSplit = !isApprovalGroup && rowCopy.secondary !== null
  const subParts = [
    isSplit ? rowCopy.secondary : notification.message,
    `updated ${timeAgo(lastAt)}`,
  ].filter(Boolean)
  if (mergedCount > 1) {
    subParts.splice(1, 0, `${mergedCount} reminders merged`)
  }

  const title = isApprovalGroup
    ? `${notifications.length} timesheets ${allOverdue ? 'are overdue for approval' : 'need your approval'}`
    : isSplit
      ? rowCopy.primary
      : notification.title
  const subtitle = isApprovalGroup
    ? `${memberCount || notifications.length} team member${(memberCount || notifications.length) === 1 ? '' : 's'} · Review the approval queue`
    : subParts.join(' · ')

  return (
    <button
      type="button"
      className="nfd-stack"
      onClick={() => onSelect(notifications)}
    >
      <span
        className={cn(
          'nfd-stack-ico',
          `nfd-stack-ico-${getActionTone(notification)}`,
        )}
      >
        {getActionIcon(notification.type)}
      </span>
      <span className="nfd-stack-body">
        {/* The panel is narrow: both lines truncate, so hover shows them whole. */}
        <span className="nfd-stack-title" title={title}>
          {title}
        </span>
        <span className="nfd-stack-sub" title={subtitle}>
          {subtitle}
        </span>
      </span>
      {isApprovalGroup && (
        <span className="nfd-count-pill">{notifications.length}</span>
      )}
      <span className="nfd-stack-cta">
        {isApprovalGroup ? 'Review all' : getActionCta(notification.type)}
      </span>
    </button>
  )
}

function FeedRow({
  notification,
  onSelect,
  showQuote,
}: {
  notification: Notification
  onSelect: (n: Notification) => void
  showQuote?: boolean
}) {
  const actorName = getActorName(notification)
  const resolution = getResolution(notification)
  const isUnread = notification.status === 'UNREAD' && !notification.dismissedAt
  const mergedCount = notification.unreadMessageCount ?? 1
  const quote = showQuote ? getNotificationQuote(notification) : null
  // Event on line 1, the record it happened to on line 2 — the stored sentence
  // buries the event at the end, where a narrow row truncates it away.
  const { primary, secondary } = getNotificationRowCopy(notification)

  return (
    <button
      type="button"
      className="nfd-row"
      onClick={() => onSelect(notification)}
    >
      <span className="nfd-row-avatar">
        {notification.actor ? (
          <UserAvatar
            user={{
              id: notification.actor.id,
              displayName: actorName ?? undefined,
              email: notification.actor.email,
            }}
            size="sm"
          />
        ) : (
          <span className="nfd-stack-ico">
            {getActionIcon(notification.type)}
          </span>
        )}
        {/* The kind badge marks a person's avatar; on a system row the icon
            already says it, and a second one repeated it (bell on bell). */}
        {notification.actor && (
          <span className="nfd-row-module">{getModuleGlyph(notification)}</span>
        )}
      </span>

      <span className="nfd-row-body">
        <span
          className={cn('nfd-row-line', resolution && 'nfd-row-line-muted')}
        >
          <span className={cn(resolution && 'nfd-row-resolved')}>
            {primary}
          </span>
          {resolution && (
            <>
              {' — '}
              <span className="nfd-row-outcome">
                {resolution.outcome.toLowerCase()}
              </span>
            </>
          )}
        </span>
        {secondary && <span className="nfd-row-record">{secondary}</span>}
        {quote && !resolution && <span className="nfd-row-quote">{quote}</span>}
        <span className="nfd-row-meta">
          {resolution
            ? `auto-resolved ${timeAgo(resolution.resolvedAt)}`
            : timeAgo(notification.createdAt)}
        </span>
      </span>

      {mergedCount > 1 && <span className="nfd-count-pill">{mergedCount}</span>}
      {isUnread && !resolution && <span className="nfd-row-unread" />}
    </button>
  )
}

// ===== the panel =====

export type NotificationFeedLane = 'action' | 'mention'

export interface NotificationFeedPanelProps {
  feed: NotificationFeedResponse
  onSelect: (notification: Notification) => void
  /** Shows a hover ✕ on each row that dismisses the notification */
  onDismiss?: (notification: Notification) => void
  /** Selects a display group; used for the combined timesheet approval queue */
  onSelectGroup?: (notifications: Notification[]) => void
  /** Dismisses every backing notification represented by a combined row */
  onDismissGroup?: (notifications: Notification[]) => void
  /** Shows "Dismiss all" on lane headers; dismisses the whole lane server-side */
  onDismissLane?: (lane: NotificationFeedLane) => void
  /** Cap items per lane (dropdown uses a smaller cap than the page) */
  maxPerLane?: number
  /** Render day rules inside the activity lane (page view) */
  showDayRules?: boolean
  className?: string
}

/**
 * Lane-based notification feed: action stacks with CTAs first, mentions with
 * quoted context second, everything else (incl. recently resolved) last.
 * Shared by the bell dropdown and the notifications page.
 */
export const NotificationFeedPanel: FC<NotificationFeedPanelProps> = ({
  feed,
  onSelect,
  onDismiss,
  onSelectGroup,
  onDismissGroup,
  onDismissLane,
  maxPerLane = 5,
  showDayRules = false,
  className,
}) => {
  const actionGroups = groupActionNotifications(feed.actionRequired).slice(
    0,
    maxPerLane,
  )
  const mentionItems = feed.mentions.slice(0, maxPerLane)
  const activityItems = feed.activity.slice(0, Math.max(maxPerLane * 2, 10))

  const isEmpty =
    actionGroups.length === 0 &&
    mentionItems.length === 0 &&
    activityItems.length === 0

  if (isEmpty) {
    return (
      <div className={cn('nfd-empty', className)}>
        <CheckCircleIcon className="mx-auto mb-2 h-8 w-8 opacity-40" />
        All caught up — nothing needs your attention
      </div>
    )
  }

  let lastDayBucket: string | null = null

  return (
    <div className={className}>
      {actionGroups.length > 0 && (
        <>
          <div className="nfd-lane nfd-lane-action">
            Action required
            <span className="nfd-lane-count">{feed.counts.actionRequired}</span>
            {onDismissLane && (
              <button
                type="button"
                className="nfd-lane-action-btn"
                onClick={() => onDismissLane('action')}
                title="Dismiss every action item. Dismissed timesheet reminders stay dismissed for that timesheet."
              >
                Dismiss all
              </button>
            )}
          </div>
          {actionGroups.map(group => {
            const isGroup = group.notifications.length > 1
            const dismiss = isGroup
              ? onDismissGroup
                ? () => onDismissGroup(group.notifications)
                : undefined
              : onDismiss
                ? () => onDismiss(group.notifications[0])
                : undefined
            return (
              <DismissableItem
                key={group.key}
                onDismiss={dismiss}
                dismissLabel={
                  isGroup
                    ? `Dismiss ${group.notifications.length} notifications`
                    : 'Dismiss notification'
                }
              >
                <ActionStack
                  notifications={group.notifications}
                  onSelect={notifications => {
                    if (notifications.length > 1 && onSelectGroup) {
                      onSelectGroup(notifications)
                    } else {
                      onSelect(notifications[0])
                    }
                  }}
                />
              </DismissableItem>
            )
          })}
        </>
      )}

      {mentionItems.length > 0 && (
        <>
          <div className="nfd-lane nfd-lane-mention">
            Mentions
            <span className="nfd-lane-count">{feed.counts.mentions}</span>
            {onDismissLane && (
              <button
                type="button"
                className="nfd-lane-action-btn"
                onClick={() => onDismissLane('mention')}
                title="Dismiss all mentions"
              >
                Dismiss all
              </button>
            )}
          </div>
          {mentionItems.map(n => (
            <DismissableItem
              key={n.id}
              onDismiss={onDismiss ? () => onDismiss(n) : undefined}
            >
              <FeedRow notification={n} onSelect={onSelect} showQuote />
            </DismissableItem>
          ))}
        </>
      )}

      {activityItems.length > 0 && (
        <>
          <div className="nfd-lane nfd-lane-activity">Everything else</div>
          {activityItems.map(n => {
            const bucket = getDayBucket(n.createdAt)
            const showRule = showDayRules && bucket !== lastDayBucket
            lastDayBucket = bucket
            return (
              <div key={n.id}>
                {showRule && <div className="nfd-day">{bucket}</div>}
                {/* Already-dismissed (resolved) rows have nothing to dismiss */}
                <DismissableItem
                  onDismiss={
                    onDismiss && !n.dismissedAt ? () => onDismiss(n) : undefined
                  }
                >
                  <FeedRow notification={n} onSelect={onSelect} />
                </DismissableItem>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
